import React from 'react';
import { useAuthContext } from './auth';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export interface APIState {
  callEndpoint: (
    path: string,
    method: 'GET' | 'POST' | 'DELETE' | 'PUT',
    payload?: unknown
  ) => Promise<unknown>;
}

const APIContext = React.createContext<APIState>({
  callEndpoint: async () => undefined
});

export const APIProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { session } = useAuthContext();

  const callEndpoint = async (
    path: string,
    method: 'GET' | 'POST' | 'DELETE' | 'PUT',
    payload?: unknown
  ): Promise<unknown> => {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    };

    if (session.token) {
      headers['Authorization'] = session.token;
    } else if (session.userId) {
      // Send anonymous user-id so the backend can associate state
      headers['X-User-Id'] = session.userId;
    }

    const response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: payload ? JSON.stringify(payload) : undefined
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    return response.json();
  };

  return (
    <APIContext.Provider value={{ callEndpoint }}>
      {children}
    </APIContext.Provider>
  );
};

export const useAPIContext = () => React.useContext(APIContext);
